import moment, { Moment } from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TimeFormat } from 'utils/enums/time-format';
import useDebounceState from 'utils/hooks/use-debounce-state';

import { InputType } from './MDatePicker.model';
import MDatePickerView from './MDatePicker.view';

type PropsBase = {
  value?: moment.Moment | null;
  showSelectedMonth?: boolean;
  hideActions?: boolean;
  withTime?: boolean;
  inputType?: InputType;
  position: 'top' | 'bottom';
  className?: string;
  inputClassName?: string;
  maxDate?: Moment;
  minDate?: Moment;
  fromYear?: number;
  toYear?: number;
  dropdown?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  invalid?: boolean;
  error?: string | null;
  closeOnSelection?: boolean;
  timezone?: string;
  showAsGmt?: boolean;
  resetTo?: Moment;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onChange: (date: moment.Moment) => void;
};

type ClearableProps = PropsBase & {
  // When clearable is true, date is optional to support 'undefined' value on clear
  clearable: true;
  onChange: (date?: moment.Moment) => void;
};

type Props = PropsBase | ClearableProps;

const MDatePicker: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const dateFormat = useMemo(() => {
    switch (props.inputType) {
      case InputType.Date:
        return 'YYYY-MM-DD';
      case InputType.DatetimeLocal:
        return 'YYYY-MM-DDTHH:mm';
      default:
        return 'YYYY-MM-DD';
    }
  }, [props.inputType]);

  const [datePickerState, debouncedDatePickerState, setDatePickerState] = useDebounceState<Moment | undefined | null>(props.value, 250);
  const [isOpenPickerState, setOpenPickerState] = useState<boolean>(false);

  const datePickerRef = useRef<HTMLDivElement>(null);
  const isSyncingFromPropsRef = useRef(false);
  const onChangeRef = useRef(props.onChange);
  const propsValueRef = useRef(props.value);

  useEffect(() => {
    onChangeRef.current = props.onChange;
    propsValueRef.current = props.value;
  }, [props.onChange, props.value]);

  const reset = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    const target = props.resetTo ? props.resetTo.clone() : props.timezone ? moment().tz(props.timezone) : moment();

    // Suppress the local→parent echo: without this, the props.value change from onChange below
    // triggers the sync effect (which has props.value in its deps) and pushes the stale
    // debouncedDatePickerState back to the parent, undoing the reset.
    isSyncingFromPropsRef.current = true;
    setDatePickerState(target);
    props.onChange(target);
  };

  const nextDay = () => {
    if (!props.value) {
      return;
    }

    // keepLocalTime: props.value is a form value (wall-clock-in-tz). During the props.timezone
    // resync gap props.value.tz() can lag behind props.timezone — bare .tz() would shift the
    // wall-clock by the offset diff before add() runs, causing a visible time jump on next-day.
    props.onChange(props.timezone ? props.value.clone().tz(props.timezone, true).add({ days: 1 }) : props.value.clone().add({ days: 1 }));
  };

  const prevDay = () => {
    if (!props.value) {
      return;
    }

    props.onChange(props.timezone ? props.value.clone().tz(props.timezone, true).add({ days: -1 }) : props.value.clone().add({ days: -1 }));
  };

  useEffect(() => {
    if (isOpenPickerState && datePickerRef.current) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          datePickerRef.current &&
          datePickerRef.current !== event.target &&
          !datePickerRef.current?.contains(event.target as HTMLElement)
        ) {
          setOpenPickerState(() => false);
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpenPickerState, datePickerRef, setOpenPickerState]);

  useEffect(() => {
    if (!debouncedDatePickerState || props.onBlur) {
      return;
    }

    if (isSyncingFromPropsRef.current) {
      // This local change originated from a parent-driven update (parent→local sync or reset),
      // not a user edit — don't echo it back to onChange.
      isSyncingFromPropsRef.current = false;

      return;
    }

    const currentValue = propsValueRef.current;

    if (currentValue?.valueOf() === debouncedDatePickerState.valueOf() && currentValue?.tz() === debouncedDatePickerState.tz()) {
      return;
    }

    onChangeRef.current(debouncedDatePickerState);
  }, [debouncedDatePickerState, props.onBlur]);

  const onDatePickerChange = (date?: Date, time?: string, isTimeOnly?: boolean) => {
    if (props.clearable && !date && !time) {
      (props as ClearableProps).onChange();

      return;
    }

    let hours = 23;
    let minutes = 59;

    if (time) {
      const [h, m] = time.split(':');

      hours = Number(h || 0);
      minutes = Number(m || 0);
    }

    let dateTime: Moment;

    if (props.timezone) {
      // Read y/m/d in props.timezone, not browser-local: the Date's instant was produced from a
      // wall time in props.timezone, so reading it in any other tz can shift the day.
      const tzMoment = moment.tz(date!, props.timezone);

      dateTime = moment.tz(
        {
          year: tzMoment.year(),
          month: tzMoment.month(),
          day: tzMoment.date(),
          hour: hours,
          minute: minutes,
        },
        props.timezone,
      );
    } else {
      dateTime = moment(date).set({ hours, minutes });
    }

    setDatePickerState(dateTime);

    if (props.closeOnSelection && !isTimeOnly) {
      setOpenPickerState(() => false);
    }
  };

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const currentTarget = e.currentTarget;
      const blurEvent = e;

      // relatedTarget is null when clicking non-focusable elements
      // Defer the check so the browser has time to settle focus on the new target.
      requestAnimationFrame(() => {
        if (currentTarget.contains(document.activeElement)) {
          return;
        }

        if (datePickerState) {
          props.onChange(datePickerState);
        }

        props.onBlur?.(blurEvent);
      });
    },
    [props.onBlur, props.onChange, datePickerState],
  );

  useEffect(() => {
    const next = props.timezone ? props.value?.clone().tz(props.timezone, true) : props.value;

    if (next?.valueOf() === datePickerState?.valueOf() && next?.tz() === datePickerState?.tz()) {
      return;
    }

    isSyncingFromPropsRef.current = true;
    setDatePickerState(next);
  }, [props.timezone, props.value]);

  return (
    <MDatePickerView
      className={props.className}
      minDate={props.minDate}
      maxDate={props.maxDate}
      inputClassName={props.inputClassName}
      withTime={props.withTime}
      time={datePickerState?.format(TimeFormat.Time)}
      isOpenPicker={isOpenPickerState}
      datePickerRef={datePickerRef}
      hideActions={props.hideActions}
      disabled={props.disabled}
      onOpen={() => setOpenPickerState(() => true)}
      onClose={() => setOpenPickerState(() => false)}
      datePicker={datePickerState}
      onDatePickerChange={onDatePickerChange}
      inputType={props.inputType}
      dateFormat={dateFormat}
      clearable={props.clearable}
      reset={reset}
      resetTo={props.resetTo}
      position={props.position}
      nextDay={nextDay}
      prevDay={prevDay}
      fromYear={props.fromYear}
      showAsGmt={props.showAsGmt}
      timezone={props.timezone}
      toYear={props.toYear}
      invalid={props.invalid}
      error={props.error}
      dropdown={props.dropdown}
      onBlur={props.onBlur ? handleBlur : undefined}
      showSelectedMonth={props.showSelectedMonth}
    ></MDatePickerView>
  );
};

MDatePicker.displayName = 'MDatePicker';
MDatePicker.defaultProps = {};

export default MDatePicker;
